import React from "react";

// Types for better type safety
export interface ButtonConfig {
  scale: string;
  gap: string;
  maxWidth: string;
}

export interface PortalHelpers {
  viewPortWidth: number;
  isArabic: boolean;
  isLargeScreen: boolean;
  buttonConfig: ButtonConfig;
}

/**
 * Calculates responsive button size configuration
 * @param viewPortWidth - Current viewport width
 * @returns ButtonConfig object with scale, gap, and maxWidth
 */
export const getButtonSize = (viewPortWidth: number): ButtonConfig => {
  if (viewPortWidth >= 768 && viewPortWidth < 920) {
    return {
      scale: "0.85", // 15% smaller
      gap: "25px", // Slightly smaller gap
      maxWidth: "calc((14rem * 2) + (2.75rem * 2))", // Smaller container
    };
  }
  return {
    scale: "1", // Normal size
    gap: "30px", // Normal gap
    maxWidth: "590px", // Normal container
  };
};

/**
 * Calculates transform for the left desktop button
 * Includes vertical offset, scaling, and language-specific horizontal positioning
 * @param isArabic - Whether current language is Arabic
 * @param isLargeScreen - Whether screen is large (>= 920px)
 * @param buttonConfig - Button configuration object
 * @returns Transform string for left button
 */
export const getLeftButtonTransform = (
  isArabic: boolean,
  isLargeScreen: boolean,
  buttonConfig: ButtonConfig
): string => {
  const translateXValue = isArabic ? "-20%" : isLargeScreen ? "0%" : "-2.5%";
  return `translateY(10%) scale(${buttonConfig.scale}) translateX(${translateXValue})`;
};

/**
 * Calculates transform for the right desktop button
 * Includes scaling and language/screen-specific horizontal positioning
 * @param isArabic - Whether current language is Arabic
 * @param isLargeScreen - Whether screen is large (>= 920px)
 * @param buttonConfig - Button configuration object
 * @returns Transform string for right button
 */
export const getRightButtonTransform = (
  isArabic: boolean,
  isLargeScreen: boolean,
  buttonConfig: ButtonConfig
): string => {
  const translateXValue = isArabic ? (isLargeScreen ? "-50%" : "-60%") : "55%";
  return `scale(${buttonConfig.scale}) translateX(${translateXValue})`;
};

/**
 * Returns flex justification for the right button container based on language
 * @param isArabic - Whether current language is Arabic
 * @returns Flex justification value
 */
export const getRightButtonJustification = (
  isArabic: boolean
): "end" | "start" => {
  return isArabic ? "end" : "start";
};

/**
 * Returns basic mobile button transform (scaling only)
 * @param buttonConfig - Button configuration object
 * @returns Transform string for mobile button
 */
export const getMobileButtonTransform = (
  buttonConfig: ButtonConfig
): string => {
  return `scale(${buttonConfig.scale})`;
};

/**
 * Returns the appropriate button component based on position and language
 * @param isLeftPosition - true for left position, false for right position
 * @param isArabic - Whether current language is Arabic
 * @param ASButton - AS Button component
 * @param RedesignLifeButton - RedesignLife Button component
 * @returns React component for the specified position
 *
 * Language-responsive button positioning:
 * Arabic (RTL): AS button (left) | RedesignLife button (right)
 * English (LTR): RedesignLife button (left) | AS button (right)
 */
export const getButtonByPosition = (
  isLeftPosition: boolean,
  isArabic: boolean,
  ASButton: React.ComponentType<any>,
  RedesignLifeButton: React.ComponentType<any>
): React.ReactElement => {
  if (isLeftPosition) {
    return React.createElement(ASButton);
  } else {
    return React.createElement(RedesignLifeButton);
  }
};

export const getSectionStyles = (width: number) => {
  if (width >= 768 && width < 920) {
    return { width: "14rem", fontSize: "16pt" };
  }
  if (width < 480) {
    return { width: "16rem", fontSize: "16pt" }; // Could be "10rem" if needed
  }
  return { width: "16rem", fontSize: "18pt" }; // Default
};
